import produce from 'immer';

import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {
  StructuredTableToTableMetadata,
  TableOpFunction,
  TableOpOptions,
} from 'notebook/tables/TableConstants';

/**
 * A utility function to update the StructuredTableMetadata on a FullTableMap, given
 * a targetTableName to make the modification on.
 *
 * @param tables the FullTableMap of tables for this entry
 * @param targetTableName the name of the table to update
 * @param metadata the StructuredTableMetadata to update the table with
 */
export function updateStructuredTableMetadataOnTableMap<TMetadata>(
  metadata: Partial<TMetadata>,
  targetTableName: string,
  tables: FullTableMap
): FullTableMap {
  if (metadata && tables[targetTableName] && tables[targetTableName].metadata) {
    return produce(tables, (draftTables) => {
      const tableMetadata = draftTables[targetTableName]
        .metadata! as StructuredTableToTableMetadata<TMetadata>;
      tableMetadata.metadata = {
        ...tableMetadata.metadata,
        ...metadata,
      };
    });
  } else if (metadata && tables[targetTableName]) {
    return produce(tables, (draftTables) => {
      draftTables[targetTableName].metadata = {
        metadata,
      } as StructuredTableToTableMetadata<TMetadata>;
    });
  } else {
    return tables;
  }
}

/**
 * This is a utility method used to update metadata for structured table.
 *
 * Should not be used if any other changes (e.g. to columns) need to be made.
 */
export default function updateStructuredTableMetadata<TMetadata>({
  performTableOpOnTable,
  metadata,
}: {
  performTableOpOnTable: ({op, options}: {op: TableOpFunction; options?: TableOpOptions}) => void;
  metadata: Partial<TMetadata>;
}): void {
  performTableOpOnTable({
    op: (originalTableMap, computedTables, targetTableName) => {
      const updatedTables = updateStructuredTableMetadataOnTableMap(
        metadata,
        targetTableName,
        originalTableMap
      );
      return {tables: updatedTables, box: null};
    },
  });
}
